import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Logo from "../images/school_logo.png";
import Loader from "./Loader";
import SuccessModal from "./SuccessModal";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable';
import { db } from "../config/fbConfig";
import { collection, getDoc, query, getDocs, where, doc, writeBatch } from "firebase/firestore";
import {SCHOOL_ACRONYM } from '../Variables.js';

const Attendance = () => {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();

  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableAttendance, setEditableAttendance] = useState({});
  const [currentWeek, setCurrentWeek] = useState(1);
  const [isDirty, setIsDirty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sectionOpenDays, setSectionOpenDays] = useState([]);

  const totalWeeks = 16;

  const calculateAttendance = (studentId) => {
    let totalPresent = 0;
    let totalDays = 0;

    const studentAttendance = editableAttendance[studentId] || {};
    
    const firstIncompleteWeek = Object.values(studentAttendance).find(
      (weekData) => weekData && !weekData.completed
    )?.week || totalWeeks + 1;

    for (let week = 1; week <= totalWeeks; week++) {
      const weekData = studentAttendance[week];
      if (!weekData) continue;

      if (weekData.completed || week === firstIncompleteWeek) {
        const daysToCount = sectionOpenDays.length;
        totalDays += daysToCount;

        sectionOpenDays.forEach((day) => {
          if (weekData.days && weekData.days[day] === true) totalPresent++;
        });
      }
    }

    const percentage = totalDays === 0 ? 0 : ((totalPresent / totalDays) * 100).toFixed(0);
    return { percentage, present: totalPresent, total: totalDays };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const sectionId = searchParams.get("section");

        // 🚀 Fetch class + section in parallel
        const [classSnap, sectionSnap] = await Promise.all([
          getDoc(doc(db, "classes", classId)),
          getDoc(doc(db, "sections", sectionId))
        ]);

        if (!classSnap.exists()) throw new Error("Class not found");
        if (!sectionSnap.exists()) throw new Error("Section not found");

        const classData = classSnap.data();
        const sectionData = sectionSnap.data();

        setClassInfo(classData);

        // ✅ Extract + ORDER days (ONLY ONCE)
        const dayOrder = [
          'monday','tuesday','wednesday',
          'thursday','friday','saturday','sunday'
        ];

        let openDaysArray = dayOrder.filter(
          day => sectionData.openDays?.[day] === true
        );
        
        if (!openDaysArray.length) {
          setError("This section has no open days configured. Please update the section.");
          setShowModal(true);
          setLoading(false);
          return;
        }

        setSectionOpenDays(openDaysArray);

        // 🚀 Fetch students in chunks (parallel)
        const studentIds = classData.students || [];
        const chunkSize = 30;

        const studentPromises = [];
        for (let i = 0; i < studentIds.length; i += chunkSize) {
          const chunk = studentIds.slice(i, i + chunkSize);
          const q = query(
            collection(db, "students"),
            where("__name__", "in", chunk)
          );
          studentPromises.push(getDocs(q));
        }

        const snapshots = await Promise.all(studentPromises);
        const studentsSnapshotDocs = snapshots.flatMap(s => s.docs);

        // ✅ Build attendance
        const initialEditableAttendance = {};

        const studentsData = studentsSnapshotDocs.map((docSnap) => {
          const studentData = docSnap.data();

          let enrollments = studentData.enrollments || [];
          let enrollment = enrollments.find((e) => e.classId === classId);

          if (!enrollment) {
            enrollment = { classId, Attendance: [] };
            enrollments = [...enrollments, enrollment];
          }

          const attendanceData = enrollment.Attendance || [];

          initialEditableAttendance[docSnap.id] = {};

          for (let week = 1; week <= totalWeeks; week++) {
            const existingWeek = attendanceData.find(w => w.week === week);

            const days = {};
            openDaysArray.forEach(day => {
              days[day] =
                existingWeek?.days?.[day] !== undefined
                  ? existingWeek.days[day]
                  : true;
            });

            initialEditableAttendance[docSnap.id][week] = {
              week,
              days,
              completed: existingWeek?.completed || false,
            };
          }

          return {
            id: docSnap.id,
            firstName: studentData.firstName || "",
            lastName: studentData.lastName || "",
            enrollments,
            Attendance: attendanceData,
          };
        });

        setEditableAttendance(initialEditableAttendance);
        setStudents(studentsData);

        // ✅ Compute first incomplete week
        const firstIncompleteWeek = studentsData.reduce((minWeek, student) => {
          const enrollment = student.enrollments?.find(e => e.classId === classId);
          const attendance = enrollment?.Attendance || [];

          const incompleteWeek =
            attendance.find(w => w && !w.completed)?.week || totalWeeks + 1;

          return Math.min(minWeek, incompleteWeek);
        }, totalWeeks + 1);

        if (currentWeek === 1) {
          setCurrentWeek(
            firstIncompleteWeek > totalWeeks ? 1 : firstIncompleteWeek
          );
        }

      } catch (error) {
        setError("Error fetching data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId]);

  const handleAttendanceChange = (studentId, week, day, value) => {
    setEditableAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [week]: {
          ...prev[studentId][week],
          days: {
            ...prev[studentId][week].days,
            [day]: value,
          },
        },
      },
    }));
    setIsDirty(true);
  };

  const saveAttendance = async () => {
    if (!isDirty) return;

    setSaving(true);
    try {
      const batch = writeBatch(db);

      students.forEach((student) => {
        const studentRef = doc(db, "students", student.id);
        let enrollments = student.enrollments || [];
        let enrollment = enrollments.find((e) => e.classId === classId) || { classId, Attendance: [] };

        const attendanceData = Object.values(editableAttendance[student.id] || {}).map((weekData) => ({
          week: weekData.week,
          days: Object.fromEntries(
            Object.entries(weekData.days).filter(([_, value]) => value !== undefined)
          ),
          completed: weekData.completed || false,
        }));

        enrollments = enrollments.some((e) => e.classId === classId)
          ? enrollments.map((e) => (e.classId === classId ? { ...e, Attendance: attendanceData } : e))
          : [...enrollments, { ...enrollment, Attendance: attendanceData }];
        batch.update(studentRef, { enrollments });
      });
      await batch.commit();

      // Update local state
      const updatedStudents = students.map((student) => {
        const enrollment = (student.enrollments || []).find((e) => e.classId === classId) || {
          classId,
          Attendance: [],
        };
        const attendanceData = Object.values(editableAttendance[student.id] || {}).map((weekData) => ({
          week: weekData.week,
          days: weekData.days,
          completed: weekData.completed,
        }));
        const updatedEnrollments = student.enrollments.some((e) => e.classId === classId)
          ? student.enrollments.map((e) =>
              e.classId === classId ? { ...e, Attendance: attendanceData } : e
            )
          : [...(student.enrollments || []), { ...enrollment, Attendance: attendanceData }];
        return {
          ...student,
          enrollments: updatedEnrollments,
        };
      });

      setStudents(updatedStudents);
      setIsDirty(false);
      setSuccessMessage("Attendance saved successfully!");
      setShowModal(true);
    } catch (error) {
      console.error("Error saving attendance:", error);
      setError("Failed to save attendance: " + error.message);
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleFinishWeek = async () => {
    setSaving(true);
    try {
      // Save current attendance data first
      await saveAttendance();

      // Mark current week as completed and update Firestore
      setEditableAttendance((prev) => {
        const updated = { ...prev };       
        Object.keys(updated).forEach((studentId) => {
          if (updated[studentId] && updated[studentId][currentWeek]) {
            updated[studentId][currentWeek].completed = true;
          }
        });
        // Build and commit Firestore batch within the callback to use updated state
        const batch = writeBatch(db);
        students.forEach((student) => {
          const studentRef = doc(db, "students", student.id);
          let enrollments = student.enrollments || [];
          let enrollment = enrollments.find((e) => e.classId === classId) || { classId, Attendance: [] };

          const attendanceData = Object.values(updated[student.id] || {}).map((weekData) => ({
            week: weekData.week,
            days: Object.fromEntries(
              Object.entries(weekData.days).filter(([_, value]) => value !== undefined)
            ),
            completed: weekData.completed || false,
          }));

          enrollments = enrollments.some((e) => e.classId === classId)
            ? enrollments.map((e) => (e.classId === classId ? { ...e, Attendance: attendanceData } : e))
            : [...enrollments, { ...enrollment, Attendance: attendanceData }];

          batch.update(studentRef, { enrollments });
        });
        batch.commit().then(() => {
          // Update local state
          const updatedStudents = students.map((student) => {
            const enrollment = (student.enrollments || []).find((e) => e.classId === classId) || {
              classId,
              Attendance: [],
            };
            const attendanceData = Object.values(updated[student.id] || {}).map((weekData) => ({
              week: weekData.week,
              days: weekData.days,
              completed: weekData.completed,
            }));
            const updatedEnrollments = student.enrollments.some((e) => e.classId === classId)
              ? student.enrollments.map((e) =>
                  e.classId === classId ? { ...e, Attendance: attendanceData } : e
                )
              : [...(student.enrollments || []), { ...enrollment, Attendance: attendanceData }];
            return {
              ...student,
              enrollments: updatedEnrollments,
            };
          });

          setStudents(updatedStudents);

          const nextIncompleteWeek = updatedStudents.reduce((minWeek, student) => {
            const enrollment = student.enrollments?.find((e) => e.classId === classId);
            const attendance = enrollment?.Attendance || [];
            const incompleteWeek = attendance.find((w) => w && !w.completed)?.week || totalWeeks + 1;
            return Math.min(minWeek, incompleteWeek);
          }, totalWeeks + 1);
          const nextWeek = nextIncompleteWeek > totalWeeks ? 1 : nextIncompleteWeek;
          setCurrentWeek(nextWeek);
          setSuccessMessage(`Week ${currentWeek} completed!`);
          setShowModal(true);
        }).catch((error) => {
          setError("Failed to mark week as completed: " + error.message);
          setShowModal(true);
        });

        return updated;
      });
    } catch (error) {
      console.error("Error finishing week:", error);
      setError("Failed to mark week as completed: " + error.message);
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  const generateAttendancePDF = () => {
    if (!classInfo || students.length === 0) return;
  
    // PDF Setup - using standard fonts to avoid encoding issues
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm'
    });
  
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const headerHeight = 30;
    
    // Calculate how many students fit vertically per page
    const studentRowHeight = 6;
    const maxStudentsPerPage = Math.floor(
      (pageHeight - margin * 2 - headerHeight - 40) / studentRowHeight // Reduced space for summaries
    );
  
    // Split weeks into chunks of 4
    const maxWeeksPerPage = 4;
    const weekChunks = [];
    for (let i = 1; i <= currentWeek; i += maxWeeksPerPage) {
      const endWeek = Math.min(i + maxWeeksPerPage - 1, currentWeek);
      weekChunks.push({ start: i, end: endWeek });
    }
  
    // Split students into chunks
    const studentChunks = [];
    for (let i = 0; i < students.length; i += maxStudentsPerPage) {
      studentChunks.push(students.slice(i, i + maxStudentsPerPage));
    }
  
    let currentPage = 0;
  
    // First pass: Render all weekly attendance pages
    weekChunks.forEach((weekChunk, weekChunkIndex) => {
      studentChunks.forEach((studentChunk, studentPageIndex) => {
        if (currentPage > 0) {
          doc.addPage('landscape');
        }
        currentPage++;
  
        // Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`${classInfo.name} ATTENDANCE RECORD`, margin, margin + 10);
        
        doc.setFontSize(12);
        doc.text(`Session: ${searchParams.get("sessionName")} | Term: ${searchParams.get("termName")}`, margin, margin + 16);
        doc.text(`Page ${currentPage}`, pageWidth - margin - 20, margin + 16);
        doc.text(`Weeks ${weekChunk.start}-${weekChunk.end}`, pageWidth / 2, margin + 16, { align: 'center' });
  
        // Table headers - just weeks and days (no summaries yet)
        const headers = [
          { 
            content: 'S/N', 
            styles: { 
              halign: 'center', 
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            } 
          },
          { 
            content: 'STUDENT NAME', 
            styles: { 
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            } 
          }
        ];
  
        // Add week headers for this chunk
        for (let week = weekChunk.start; week <= weekChunk.end; week++) {
          headers.push({
            content: `Week ${week}`,
            colSpan: sectionOpenDays.length,
            styles: {
              halign: 'center',
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            }
          });
        }
  
        const subHeaders = [
          { content: '', styles: { fillColor: [220, 220, 220] } },
          { content: '', styles: { fillColor: [220, 220, 220] } }
        ];

  
        // Add day headers for each week in this chunk
        for (let week = weekChunk.start; week <= weekChunk.end; week++) {
          sectionOpenDays.forEach(day => {
            subHeaders.push({
              content: day.substring(0, 3).toUpperCase(), // MON, TUE, etc.
              styles: { 
                halign: 'center', 
                fillColor: [240, 240, 240],
                lineWidth: 0.1,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0]
              }
            });
          });
        }
  
        // Prepare table data - just attendance, no summaries yet
        const body = studentChunk.map((student, idx) => {
          const globalIndex = studentPageIndex * maxStudentsPerPage + idx;
          
          const row = [
            { content: globalIndex + 1, styles: { halign: 'center', textColor: [0, 0, 0] } },
            { content: `${student.firstName} ${student.lastName}`, styles: { textColor: [0, 0, 0] } }
          ];
  
          // Add attendance for each week in this chunk        
          for (let week = weekChunk.start; week <= weekChunk.end; week++) {
            const weekData = editableAttendance[student.id]?.[week] || {};
            
            sectionOpenDays.forEach(day => {
              const isPresent = weekData.days?.[day];
              row.push({
                content: isPresent ? 'O' : 'X',
                styles: { 
                  halign: 'center',
                  fontStyle: 'bold',
                  textColor: isPresent ? [0, 128, 0] : [255, 0, 0]
                }
              });
            });
          }
  
          return row;
        });
  
        // Generate the table
        autoTable(doc, {
          startY: margin + headerHeight,
          head: [headers, subHeaders],
          body: body,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            font: 'helvetica'
          },
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            font: 'helvetica'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
            font: 'helvetica'
          },
          margin: { left: margin }
        });
  
        // Footer
        const now = new Date();
        const options = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        const formattedDate = now.toLocaleDateString('en-US', options);
  
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 10, { align: 'center' });
        doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, pageHeight - 10);
      });
    });
  
    // Add a new page for student summaries (after all weekly attendance)
    doc.addPage('landscape');
    
    // Header for student summaries
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`${classInfo.name} STUDENT ATTENDANCE SUMMARIES`, margin, margin + 10);
    
    doc.setFontSize(12);
    doc.text(`Session: ${searchParams.get("sessionName")} | Term: ${searchParams.get("termName")}`, margin, margin + 16);
    doc.text(`Up to Week ${currentWeek}`, pageWidth / 2, margin + 16, { align: 'center' });
  
    // Prepare student summary data
    const summaryHeaders = [
      { 
        content: 'S/N', 
        styles: { 
          halign: 'center', 
          fillColor: [220, 220, 220],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],          
        } 
      },
      { 
        content: 'STUDENT NAME', 
        styles: { 
          fillColor: [220, 220, 220],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        } 
      },
      { 
        content: 'PRESENT DAYS', 
        styles: { 
          halign: 'center',
          fillColor: [220, 220, 220],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        } 
      },
      { 
        content: 'TOTAL DAYS', 
        styles: { 
          halign: 'center',
          fillColor: [220, 220, 220],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        } 
      },
      { 
        content: 'PERCENTAGE', 
        styles: { 
          halign: 'center',
          fillColor: [220, 220, 220],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        } 
      }
    ];
  
    const summaryBody = students.map((student, idx) => {
      const { percentage, present, total } = calculateAttendance(student.id);
      return [
        { content: idx + 1, styles: { halign: 'center', textColor: [0, 0, 0] } },
        { content: `${student.firstName} ${student.lastName}`, styles: {textColor: [0, 0, 0]} },
        { content: present, styles: { halign: 'center', fontStyle: 'bold', textColor: [0, 0, 0] } },
        { content: total, styles: { halign: 'center', fontStyle: 'bold', textColor: [0, 0, 0] } },
        { content: `${percentage}%`, styles: { halign: 'center', fontStyle: 'bold', textColor: [0, 0, 0] } }
      ];
    });
  
    // Generate student summaries table
    autoTable(doc, {
      startY: margin + headerHeight,
      head: [summaryHeaders],
      body: summaryBody,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      margin: { left: margin }
    });
  
    // Footer for student summaries page
    const now = new Date();
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
  
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 10, { align: 'center' });
    doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, pageHeight - 10);
  
    // Add class summary on a new page after all other pages
    doc.addPage('landscape');
    
    // Header for class summary
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`${classInfo.name} CLASS ATTENDANCE SUMMARY`, margin, margin + 10);
    
    doc.setFontSize(12);
    doc.text(`Session: ${searchParams.get("sessionName")} | Term: ${searchParams.get("termName")}`, margin, margin + 16);
    doc.text(`Up to Week ${currentWeek}`, pageWidth / 2, margin + 16, { align: 'center' });
  
    // Calculate class statistics
    let totalPresent = 0;
    let totalDays = 0;
    
    students.forEach(student => {
      const { present, total } = calculateAttendance(student.id);
      totalPresent += present;
      totalDays += total;
    });
  
    const classPercentage = totalDays > 0 ? Math.round((totalPresent / totalDays) * 100) : 0;
    
    // Class statistics table
    autoTable(doc, {
      startY: margin + headerHeight,
      head: [['Class Attendance Summary', 'Value']],
      body: [
        ['Total Present Days', totalPresent],
        ['Total Possible Days', totalDays],
        ['Class Attendance Percentage', `${classPercentage}%`]
      ],
      styles: { 
        fontSize: 10, 
        cellPadding: 4,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center',
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      headStyles: { 
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60, font: 'helvetica', textColor: [0, 0, 0] },
        1: { halign: 'center', cellWidth: 30, font: 'helvetica', textColor: [0, 0, 0] }
      },
      margin: { left: margin },
      tableWidth: 90
    });
  
    // Footer for class summary page
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 10, { align: 'center' });
    doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, pageHeight - 10);
  
    doc.save(`${classInfo.name}_Attendance_${searchParams.get("sessionName")}_${searchParams.get("termName")}.pdf`);
  };  

  if (loading) {
    return <Loader text="LOADING" />;
  }

  return (
    <>
      <div className="scroll-fixed" style={{ minHeight: "100vh" }}>
        <div className="top-elements row align-center space-between">
          <Link to="/home" className="back-link">
            <i className="fa-solid fa-arrow-left size3"></i>
          </Link>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>
        <div style={{ maxWidth: "100vw", padding: "20px", marginTop: "80px" }}>
          <p className="male-female-stat">ATTENDANCE - {classInfo?.name} ({searchParams.get("sessionName")} - {searchParams.get("termName")} Term)</p>          
          <div className="row align-center gap-1" style={{ marginBottom: "10px" }}>
            <select
              value={currentWeek}
              onChange={(e) => setCurrentWeek(Number(e.target.value))}
              className='male-female-stat'
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((week) => (
                <option className='male-female-stat' key={week} value={week}>
                  Week {week}{editableAttendance[students[0]?.id]?.[week]?.completed && " ✓"}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleFinishWeek}
              disabled={saving || editableAttendance[students[0]?.id]?.[currentWeek]?.completed}
              className='male-female-stat'
              style={{
                padding: "8px 16px",
                backgroundColor: editableAttendance[students[0]?.id]?.[currentWeek]?.completed
                  ? "#cccccc"
                  : "rgb(224, 193, 107)",
                color: editableAttendance[students[0]?.id]?.[currentWeek]?.completed ? "#111d2bad" : "#111d2b",
                border: "none",
                borderRadius: "4px",
                cursor: editableAttendance[students[0]?.id]?.[currentWeek]?.completed ? "not-allowed" : "pointer",
                marginLeft: "10px",
              }}
            >
              Finish Week
            </button>
            <button
              onClick={generateAttendancePDF}
              className='male-female-stat'
              style={{
                padding: "8px 16px",
                backgroundColor: "rgb(224, 193, 107)",
                color: "#111d2b",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px",
              }}
            >
              Print
            </button>
          </div>
          
          <div className="scroll-fixed scroll-fixed1" style={{ width: "100%", marginTop: "10px" }}>
            <table
              className='male-female-stat'
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.8rem",
                minWidth: "300px",
              }}
            >
              <thead>
                <tr className="fees-table" style={{ backgroundColor: "#f2f2f2" }}>
                  <th
                    style={{
                      padding: "4px",
                      border: "1px solid #ddd",
                      textAlign: "center",
                      width: "30px",
                    }}
                  >
                    S/N
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      border: "1px solid #ddd",
                      textAlign: "left",
                      width: "120px",
                    }}
                  >
                    Student
                  </th>
                  {sectionOpenDays.map((day) => (
                    <th
                      key={day}
                      style={{
                        padding: "4px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                        width: "30px",
                      }}
                    >
                      {(() => {
                        const short = day.substring(0, 3);
                        return short.charAt(0).toUpperCase() + short.slice(1);
                      })()}
                    </th>
                  ))}
                  <th
                    style={{
                      padding: "4px",
                      border: "1px solid #ddd",
                      textAlign: "center",
                      width: "60px",
                    }}
                  >
                    %
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      border: "1px solid #ddd",
                      textAlign: "center",
                      width: "60px",
                    }}
                  >
                    Days
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const { percentage, present, total } = calculateAttendance(student.id);
                  return (
                    <tr className='male-female-stat' key={student.id} style={{ borderBottom: "1px solid #ddd" }}>
                      <td style={{ padding: "4px", textAlign: "center" }}>{index + 1}</td>
                      <td
                        style={{
                          padding: "4px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {student.firstName} {student.lastName}
                      </td>                      
                      {sectionOpenDays.map((day) => (
                        <td
                          key={`${student.id}-${day}`}
                          style={{ padding: "4px", textAlign: "center" }}
                        >
                          <input
                            type="checkbox"
                            checked={editableAttendance[student.id]?.[currentWeek]?.days[day] ?? true}
                            onChange={(e) => handleAttendanceChange(student.id, currentWeek, day, e.target.checked)}
                            style={{ width: "16px", height: "16px", accentColor: "rgb(224, 193, 107)"  }}
                          />
                        </td>
                      ))}
                      <td style={{ padding: "4px", textAlign: "center" }}>
                        {percentage}%
                      </td>
                      <td style={{ padding: "4px", textAlign: "center" }}>
                        {present}/{total}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
              onClick={saveAttendance}
              disabled={saving || !isDirty}
              className='male-female-stat'
              style={{
                padding: "8px 16px",
                backgroundColor: isDirty ? "rgb(224, 193, 107)" : "#cccccc",
                color: isDirty ? "#111d2b" : "#111d2bad",
                border: "none",
                borderRadius: "4px",
                cursor: isDirty ? "pointer" : "not-allowed",
                marginLeft: "10px",
                marginTop: "10px",
                marginRight: "10px"
              }}
            >
              {saving ? "Saving Attendance" : "Save Attendance"}
            </button>
            {isDirty && <span className="size5 male-female-stat" style={{ color: "#666" }}>You have unsaved changes</span>}
        </div>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setShowModal(false);
            setError("");
            setSuccessMessage("");
          }}
        />
      )}
    </>
  );
};

export default Attendance;